import { addClientInstallation } from '@api/services/client-installation.service';
import { useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';
import moment from 'moment';

const useClientInstallationAddForm = () => {
    const schema = Yup.object({
        name: Yup.string().required('Name is required'),
        poc: Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                email: Yup.string().email('Invalid email').required('Email is required'),
                phoneNumber: Yup.number().nullable(),
                designation: Yup.string(),
            })
        ),
        location: Yup.object({
            country: Yup.string().required('Country is required'),
            latitude: Yup.string(),
            longitude: Yup.string(),
            googleMapsLink: Yup.string().url('Enter a valid URL'),
        }),
        contract: Yup.object({
            startDate: Yup.string()
                .nullable()
                .test('valid-startDate', 'Invalid start date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                }),

            endDate: Yup.string()
                .nullable()
                .test('valid-endDate', 'Invalid end date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                })
                .test('end-after-start', 'End date must be same or after start date', function (endDate) {
                    const { startDate } = this.parent;

                    if (!startDate || !endDate) return true; // Allow empty or null start or end date
                    const start = moment(startDate, 'YYYYMMDD', true);
                    const end = moment(endDate, 'YYYYMMDD', true);

                    // Ensuring the end date is same or after the start date
                    return end.isSameOrAfter(start, 'day');
                }),
        }),

        businessDevelopmentUser: Yup.string().required('This field is required'),
        files: Yup.array().of(Yup.mixed()),
    });

    const initialValues: {
        name: string;
        poc: { name: string; email: string; phoneNumber: number | string; designation: string }[];
        location: {
            country: string;
            latitude: string;
            longitude: string;
            googleMapsLink: string;
        };
        contract: {
            startDate: string;
            endDate: string;
        };
        businessDevelopmentUser: string;
        files: File[];
    } = {
        name: '',
        poc: [],
        location: {
            country: '',
            latitude: '',
            longitude: '',
            googleMapsLink: '',
        },
        contract: {
            startDate: '',
            endDate: '',
        },
        businessDevelopmentUser: '',
        files: [],
    };

    const mutation = useMutation((payload: FormData) => addClientInstallation(payload));

    const submit = async (values: typeof initialValues) => {
        const formData = new FormData();

        formData.append('name', values.name);

        values.poc.forEach((poc, index) => {
            formData.append(`poc[${index}][name]`, poc.name);
            formData.append(`poc[${index}][email]`, poc.email);
            formData.append(`poc[${index}][phoneNumber]`, poc.phoneNumber.toString());
            formData.append(`poc[${index}][designation]`, poc.designation);
        });

        formData.append('location[country]', values.location.country);
        if (values.location.latitude) formData.append('location[latitude]', values.location.latitude);
        if (values.location.longitude) formData.append('location[longitude]', values.location.longitude);
        if (values.location.googleMapsLink) formData.append('location[googleMapsLink]', values.location.googleMapsLink);

        if (values.contract.startDate) formData.append('contract[startDate]', values.contract.startDate);

        if (values.contract.endDate) formData.append('contract[endDate]', values.contract.endDate);

        formData.append('businessDevelopmentUser', values.businessDevelopmentUser);

        values.files?.forEach((file: File) => {
            formData.append('files', file);
        });

        const { data } = await mutation.mutateAsync(formData);
        return data;
    };

    return {
        initialValues,
        schema,
        isSubmitting: mutation.isLoading,
        submit,
    };
};

export default useClientInstallationAddForm;
